import React, { useState } from 'react';
import { Button, Divider, Dropdown, Flex, Table, Tooltip, Typography } from 'antd';
import { Cell, Header, HeaderCell, Row } from '../../../../../../features/shared/components/Table';
import { format } from 'date-fns';
import { DeleteOutlined, MinusOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { colors } from '../../../../../../theme/colors';
import { Framework } from '../../../../../../features/frameworks/type';

import Style from './style';
const { Container, NoDataContainer, Message, Title, Detail } = Style;

interface Props {
  data: Framework[];
  loading?: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const FrameworkTable: React.FC<Props> = ({ data, loading, onEdit, onDelete }) => {
  const [expandRow, setExpandRow] = useState<string[]>([]);

  const items = (id: string) => [
    {
      key: '1',
      label: <a onClick={() => onEdit(id)}>Edit</a>,
    },
    {
      key: '-',
      label: <Divider style={{ margin: '0px', width: '100%' }} />,
      disabled: true,
    },
    {
      key: '2',
      label: (
        <a
          style={{ color: 'red', display: 'flex', alignItems: 'center', gap: '5px' }}
          onClick={() => onDelete(id)}
        >
          {loading ? (
            <Button loading={loading} />
          ) : (
            <>
              <DeleteOutlined style={{ color: 'red' }} /> Delete
            </>
          )}
        </a>
      ),
    },
  ];

  const columns: ColumnsType<Framework> = [
    {
      title: 'Framework Name',
      key: 'name',
      render: (value: string, framework: Framework) => <>{framework.name}</>,
    },
    {
      title: 'Meeting Types Assigned',
      key: 'meeting-type',
      width: '300px',
      render: (value: string, framework: Framework) => (
        <>
          <span style={{ color: 'lightgrey', fontWeight: 'normal' }}>
            <em>{!framework.meetingType.length ? 'No Meeting Types Assigned' : ''}</em>
          </span>

          {framework.meetingType.length > 1 ? (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {framework.meetingType[0]}
              <Tooltip
                overlayStyle={{ maxWidth: '900px' }}
                title={
                  <div
                    style={{ whiteSpace: 'pre-wrap' }}
                  >{`All Meeting Types Assigned \n\n${framework.meetingType
                    .map((m: string, i) => `${i + 1}. ${m}\n`)
                    .join('')}`}</div>
                }
              >
                <a style={{ color: colors.primary[500] }}>+{framework.meetingType.length - 1} </a>
              </Tooltip>
            </div>
          ) : (
            framework.meetingType
          )}
        </>
      ),
    },
    {
      title: 'Created By',
      key: 'created-by',
      render: (v, r: Framework) => (
        <>
          {r.createdBy ? (
            <span>
              {r.createdBy?.firstName} {r.createdBy?.lastName}
            </span>
          ) : (
            <span style={{ color: 'lightgrey' }}>
              <em>No Owner Available</em>
            </span>
          )}
        </>
      ),
    },
    {
      title: 'Created At',
      key: 'created-at',
      width: '300px',
      render: (v, r: Framework) => (
        <div>
          <>{format(new Date(r.createdAt), 'dd MMMM, yyyy - hh:mm')} </>
          <>{format(new Date(r.createdAt), 'aaaa').toUpperCase()}</>
        </div>
      ),
    },
    {
      key: 'action',
      width: '50px',
      render: (v, r) => (
        <Dropdown menu={{ items: items(r.id) }} placement="bottomRight" disabled={loading}>
          <Button loading={loading} icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  const components = {
    header: {
      row: Header,
      cell: HeaderCell,
    },
    body: {
      row: Row,
      cell: Cell,
    },
  };

  return (
    <Container>
      {data.length > 0 || loading ? (
        <Table
          loading={loading}
          columns={columns}
          pagination={false}
          dataSource={data}
          components={components}
          rowKey={(framework: Framework) => framework.id}
          expandable={{
            expandedRowKeys: expandRow,
            expandIcon: (row: { expanded: boolean; record: Framework }) => (
              <Button
                size="small"
                icon={row.expanded ? <MinusOutlined /> : <PlusOutlined />}
                onClick={() => {
                  if (expandRow.includes(row.record.id)) {
                    setExpandRow(expandRow.filter(id => id !== row.record.id));
                  } else {
                    setExpandRow([...expandRow, row.record.id]);
                  }
                }}
              />
            ),
            expandedRowRender: (record: Framework) => (
              <div>
                <Typography.Title level={4} style={{ margin: 0, marginBottom: '24px' }}>
                  Framework Questions
                </Typography.Title>

                <Flex vertical gap={16}>
                  {record.questions.map((question, index) => (
                    <Flex
                      vertical
                      gap={4}
                      key={question.id}
                      style={{
                        borderBottom:
                          index === record.questions.length - 1
                            ? 'none'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        paddingBottom: '24px',
                      }}
                    >
                      <Typography.Title level={5} style={{ margin: 0, fontSize: '18px' }}>
                        {question.value}
                      </Typography.Title>
                      {question.context ? (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{question.context}</span>
                      ) : (
                        <span style={{ color: 'lightgrey', fontStyle: 'italic' }}>
                          No context provided
                        </span>
                      )}
                    </Flex>
                  ))}
                </Flex>
              </div>
            ),
          }}
        />
      ) : (
        <NoDataContainer>
          <Message>
            <Title>No data</Title>
            <Detail>It seems that there are no frameworks created yet.</Detail>
          </Message>
        </NoDataContainer>
      )}
    </Container>
  );
};

export default FrameworkTable;
